import { type Change, type ChangeFlags, generateKey } from '@gitbook/slate';
import type { DeepReadonly } from '@gitbook/util-immutable';
import {
    createOrderIndex,
    findInsertPositionInSortedOrderIndexes,
    type Orderable,
    type OrderIndexPosition,
} from '@gitbook/util-orderindex';

import type {
    BlockTableData,
    TableCellMerge,
    TableColumnDefinition,
    TableRecord,
    TableView,
} from '../types';
import {
    canInsertAtCellMergeBands,
    canInsertAtCellMergeBoundary,
    getCellMergeBands,
    isColumnPartOfCellMerge,
    isRecordPartOfCellMerge,
    preservesCellMergeOrder,
    resolveCellMerges,
} from './mergeConstraints';
import { createColumnFromType, defaultRecordSortedKeys, getLastRecordOrderIndex } from './utils';

/**
 * Insert a column in a table.
 */
export function insertColumn(
    change: Change,
    tableKey: string,

    /**
     * Column definition to insert.
     */
    definition: TableColumnDefinition = createColumnFromType('text'),

    /**
     * Index in the view.columns array at which to insert the new visible column.
     * If none is provided, the column will be inserted at the end.
     */
    atIndex?: number
): boolean {
    const data = getCurrentTableData(change, tableKey);
    const index = getColumnInsertionIndex(data.view.columns, atIndex);
    if (!canInsertAtCellMergeBoundary(data, 'columns', data.view.columns, index)) return false;

    change.insertDataByKey(tableKey, ['definition', definition.id], definition, {
        normalize: false,
    });

    // Insert the column at the correct position
    change.insertArrayDataByKey(tableKey, ['view', 'columns'], definition.id, index);
    return true;
}

/**
 * Insert a new empty record into a table. It returns the ID of the newly created record.
 */
export function insertEmptyRecord(
    change: Change,
    tableKey: string,
    at?: OrderIndexPosition
): string | undefined {
    const data = getCurrentTableData(change, tableKey);
    const insertion = createEmptyRecordInsertion(data, at);
    if (
        !canInsertAtCellMergeBoundary(
            data,
            'records',
            defaultRecordSortedKeys(data),
            insertion.index
        )
    ) {
        return undefined;
    }

    change.insertDataByKey(tableKey, ['records', insertion.id], insertion.record);
    return insertion.id;
}

export interface InsertEmptyRecordsOptions {
    /**
     * Number of empty records to insert.
     */
    count: number;
    /**
     * Base position used for the first insertion.
     */
    position: OrderIndexPosition;
    /**
     * Anchor side to advance after each insertion.
     * - 'before': advance the `before` index so the next record is appended after the last inserted one.
     * - 'after': advance the `after` index so the next record is inserted before the last inserted one.
     */
    anchor: 'before' | 'after';
}

/**
 * Insert multiple empty records, returning the IDs in insertion order.
 */
export function insertEmptyRecords(
    change: Change,
    tableKey: string,
    options: InsertEmptyRecordsOptions
): string[] | null {
    const { count, position, anchor } = options;
    const total = Math.max(0, count);

    if (total === 0) {
        return [];
    }

    const insertions: Array<{ id: string; record: TableRecord }> = [];
    const data = getCurrentTableData(change, tableKey);

    // Sort the records and resolve the merge bands once: merges cannot change while we insert,
    // and the ordered keys are maintained incrementally below.
    const recordOrder = defaultRecordSortedKeys(data);
    const mergeBands = getCellMergeBands(data, 'records', recordOrder);
    const orderedRecords: Orderable[] = recordOrder.map((recordId) => data.records[recordId]);

    const fixedBefore = anchor === 'before' ? undefined : position.before;
    const fixedAfter = anchor === 'after' ? undefined : position.after;
    let anchorOrderIndex = anchor === 'before' ? position.before : position.after;

    for (let index = 0; index < total; index += 1) {
        const at: OrderIndexPosition = {};

        if (anchor === 'before') {
            if (anchorOrderIndex) {
                at.before = anchorOrderIndex;
            }
            if (fixedAfter) {
                at.after = fixedAfter;
            }
        } else {
            if (fixedBefore) {
                at.before = fixedBefore;
            }
            if (anchorOrderIndex) {
                at.after = anchorOrderIndex;
            }
        }

        const id = generateKey();
        const record: TableRecord = { values: {}, orderIndex: createOrderIndex(at) };
        const insertionIndex = findInsertPositionInSortedOrderIndexes(orderedRecords, record);

        if (!canInsertAtCellMergeBands(mergeBands, insertionIndex)) {
            return null;
        }

        insertions.push({ id, record });
        orderedRecords.splice(insertionIndex, 0, record);
        for (const band of mergeBands) {
            if (band.start >= insertionIndex) {
                band.start += 1;
            }
        }
        anchorOrderIndex = record.orderIndex;
    }

    for (const insertion of insertions) {
        change.insertDataByKey(tableKey, ['records', insertion.id], insertion.record, {
            normalize: false,
        });
    }
    change.normalizeNodeByKey(tableKey);
    return insertions.map((insertion) => insertion.id);
}

/**
 * Move a record to another position.
 */
export function moveRecord(
    change: Change,
    tableKey: string,
    recordId: string,
    at: OrderIndexPosition
): boolean {
    const data = getCurrentTableData(change, tableKey);
    const record = data.records[recordId];
    if (!record) return false;

    const records = {
        ...data.records,
        [recordId]: { ...record, orderIndex: createOrderIndex(at) },
    };
    const updatedData = { ...data, records };
    if (!preservesCellMergeOrder(data, defaultRecordSortedKeys(updatedData), data.view.columns)) {
        return false;
    }

    change.setDataByKey(
        tableKey,
        ['records', recordId, 'orderIndex'],
        records[recordId].orderIndex
    );
    return true;
}

/**
 * Move a column in the grid or cards view.
 * Accepts the id of the column to move and the id of the column where it will be dropped.
 */
export function moveColumn({
    change,
    tableKey,
    columnId,
    beforeColumnId,
}: {
    change: Change;
    tableKey: string;
    columnId: string;
    beforeColumnId: string;
}): boolean {
    const data = getCurrentTableData(change, tableKey);
    const columns = getMovedColumnOrder(data.view, columnId, beforeColumnId);
    if (!columns) return false;
    if (!preservesCellMergeOrder(data, defaultRecordSortedKeys(data), columns)) return false;

    change.setDataByKey(tableKey, ['view', 'columns'], columns);
    return true;
}

/** Return the column order produced by moving one column onto another. */
export function getMovedColumnOrder(
    view: { type: TableView['type']; columns: readonly string[] },
    columnId: string,
    beforeColumnId: string
): string[] | null {
    const columns = [...view.columns];
    const fromIndex = columns.indexOf(columnId);
    const beforeIndex = columns.indexOf(beforeColumnId);
    if (fromIndex < 0 || beforeIndex < 0) {
        return null;
    }

    const newIndex =
        fromIndex < beforeIndex || view.type === 'cards' ? beforeIndex : beforeIndex + 1;
    columns.splice(fromIndex, 1);
    columns.splice(newIndex, 0, columnId);
    return columns;
}

/**
 * Hide a column from a view.
 */
export function hideColumn(change: Change, tableKey: string, columnId: string): boolean {
    const data = change.value.document
        .assertBlock(tableKey)
        .data.toFrozenObject() as BlockTableData;
    if (isColumnPartOfCellMerge(data, columnId)) return false;

    change.removeArrayDataByKey(tableKey, ['view', 'columns'], columnId);
    return true;
}

/**
 * Show a previously hidden column in a table.
 */
export function showColumn({
    change,
    tableKey,
    columnId,
    atIndex,
}: {
    change: Change;
    tableKey: string;
    columnId: string;
    atIndex?: number;
}): boolean {
    const data = getCurrentTableData(change, tableKey);
    const originalColumn = data.definition[columnId];

    if (!originalColumn || data.view.columns.includes(columnId)) return false;

    // Calculate insert position if not provided
    const insertIndex = getColumnInsertionIndex(data.view.columns, atIndex ?? 0);
    if (!canInsertAtCellMergeBoundary(data, 'columns', data.view.columns, insertIndex)) {
        return false;
    }

    // Insert the column at the correct position
    change.insertArrayDataByKey(tableKey, ['view', 'columns'], columnId, insertIndex);
    return true;
}

/**
 * Remove a column from a table.
 */
export function removeColumn(change: Change, tableKey: string, columnId: string): boolean {
    const data = getCurrentTableData(change, tableKey);
    if (!data.definition[columnId] || isColumnPartOfCellMerge(data, columnId)) return false;

    // Remove it from the definition, let validation take care of the rest.
    change.removeDataPathByKey(tableKey, ['definition', columnId]);
    return true;
}

/**
 * Change the type of a column.
 */
export function setColumnDefinition(
    change: Change,
    tableKey: string,
    columnId: string,
    def: TableColumnDefinition,
    options?: ChangeFlags
) {
    const data = change.value.document
        .assertBlock(tableKey)
        .data.toFrozenObject() as BlockTableData;
    const currentDefinition = data.definition[columnId];
    if (currentDefinition && currentDefinition.type !== def.type) {
        const affectedMerges = resolveCellMerges(
            data,
            defaultRecordSortedKeys(data),
            data.view.columns
        ).filter((merge) => merge.columns.includes(columnId));
        const affectedMetadata = new Set(affectedMerges.map((merge) => merge.merge));
        const remainingMerges = (data.cellMerges ?? []).filter(
            (merge) => !affectedMetadata.has(merge)
        );
        if (affectedMerges.length > 0 && remainingMerges.length === 0) {
            change.removeDataByKey(tableKey, ['cellMerges'], options);
        } else if (affectedMerges.length > 0) {
            change.setDataByKey(tableKey, ['cellMerges'], remainingMerges, options);
        }
    }
    change.setDataByKey(tableKey, ['definition', columnId], { ...def, id: columnId }, options);
}

/**
 * Removes a record from the table.
 */
export function removeRecord(change: Change, tableKey: string, recordId: string): boolean {
    const data = getCurrentTableData(change, tableKey);
    if (!data.records[recordId] || isRecordPartOfCellMerge(data, recordId)) return false;

    change.removeDataPathByKey(tableKey, ['records', recordId]);
    return true;
}

/**
 * Sets the value of a record for a specific column.
 */
export function setRecordValue(
    change: Change,
    tableKey: string,
    recordId: string,
    colId: string,
    value: any,
    options?: ChangeFlags
) {
    change.insertDataByKey(tableKey, ['records', recordId, 'values', colId], value, options);
}

/**
 * Sets the value of a record for a specific column.
 */
export function renameColumn(change: Change, tableKey: string, columnId: string, title: string) {
    change.insertDataByKey(tableKey, ['definition', columnId, 'title'], title);
}

/** Return whether a table view can represent normalized merge metadata safely. */
export function canUseViewWithCellMerges(
    data: {
        cellMerges?: readonly TableCellMerge[];
    },
    viewType: TableView['type']
): boolean {
    return viewType === 'grid' || !data.cellMerges?.length;
}

/**
 * Change the view in a table.
 */
export function setView(
    change: Change,
    tableKey: string,
    view: Partial<DeepReadonly<TableView>> & Pick<TableView, 'type'>
): boolean {
    const tableData = change.value.document
        .assertBlock(tableKey)
        .data.toFrozenObject() as BlockTableData;
    if (!canUseViewWithCellMerges(tableData, view.type)) return false;
    const currentView = tableData.view;

    // Automatically preserve all existing view properties unless explicitly overridden
    const updatedView = {
        ...currentView,
        ...view,
        columns: view.columns || currentView.columns, // Ensure columns are preserved
    };
    if (
        !preservesCellMergeOrder(tableData, defaultRecordSortedKeys(tableData), updatedView.columns)
    ) {
        return false;
    }

    change.setDataByKey(tableKey, ['view'], updatedView);
    return true;
}

interface EmptyRecordInsertion {
    id: string;
    record: TableRecord;
    index: number;
}

function createEmptyRecordInsertion(
    data: BlockTableData,
    at?: OrderIndexPosition
): EmptyRecordInsertion {
    const id = generateKey();
    const position =
        at ??
        (Object.keys(data.records).length > 0
            ? { before: getLastRecordOrderIndex(data) }
            : undefined);
    const record = { values: {}, orderIndex: createOrderIndex(position) };
    const updatedData = {
        ...data,
        records: { ...data.records, [id]: record },
    };
    return { id, record, index: defaultRecordSortedKeys(updatedData).indexOf(id) };
}

function getColumnInsertionIndex(columns: readonly string[], atIndex?: number): number {
    return typeof atIndex === 'number'
        ? Math.min(Math.max(atIndex, 0), columns.length)
        : columns.length;
}

function getCurrentTableData(change: Change, tableKey: string): BlockTableData {
    return change.value.document.assertBlock(tableKey).data.toFrozenObject() as BlockTableData;
}
